<template>
  <context-menu
    v-if="currentConfig"
    :key="`${currentConfig.controlNodeId}-${isSidebarOpen}`"
    :position="currentConfig.positionContext"
    :style="(currentConfig.type === NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR || currentConfig.type === NOTIFICATION_POPUP_TYPE.SUPPORT_HINT)
    ? { padding: '0 !important',
     overflow: 'visible !important',
     backgroundColor: 'transparent !important',
     boxShadow: 'none !important',
     minWidth: 'auto !important',
     maxHeight: 'none !important',
     border: 'none !important' }
    : { padding: 0, overflowY: 'unset' }"
    :side="(currentConfig.type === NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR || currentConfig.type === NOTIFICATION_POPUP_TYPE.SUPPORT_HINT) ? 'right' : ''"
    :horizontally-align-center="(currentConfig.type !== NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR && currentConfig.type !== NOTIFICATION_POPUP_TYPE.SUPPORT_HINT)"
    :offset="(currentConfig.type === NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR || currentConfig.type === NOTIFICATION_POPUP_TYPE.SUPPORT_HINT) ? { top: 0 } : { top: -6 }"
    :disable-auto-positioning="(currentConfig.type === NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR || currentConfig.type === NOTIFICATION_POPUP_TYPE.SUPPORT_HINT)"
  >
    <template #body>
      <notify-popup-slot
        v-bind="currentConfig"
        :type="type"
        :is-sidebar-collapsed="currentConfig.isSidebarCollapsed"
        @onClose="handleOnCloseNotificationPopup"
        @onClick="handleOnClickNotificationPopup"
        @onMouseOver="toggleControlHighlight(true, $event)"
        @onMouseLeave="toggleControlHighlight(false, $event)"
      />
    </template>
  </context-menu>
</template>

<script>

import { api } from '$$store/api';
import { cloneDeep, throttle } from 'lodash';
import { NOTIFICATION_POPUP_CONFIG, NOTIFICATION_POPUP_TYPE } from './notifyPopup.config';
import NotifyPopupSlot from './notifyPopupSlot.vue';

export default {
  name: 'NotifyPopup',
  components: {
    NotifyPopupSlot,
  },
  props: {
    isShown: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
      validator(value) {
        return Object.values(NOTIFICATION_POPUP_TYPE).includes(value);
      },
    },
    isSidebarOpen: {
      type: Boolean,
      default: true,
    },
  },
  emits: {
    close: null,
    onClick: String,
    highlight: null,
  },
  data() {
    return {
      defaultConfigMap: NOTIFICATION_POPUP_CONFIG,
      currentConfig: null,
      NOTIFICATION_POPUP_TYPE,
      sidebarObserver: null,
    };
  },
  watch: {
    type: {
      handler(val) {
        if (val) {
          this.setNotificationConfigByType(val);
        }
      },
      immediate: true,
    },
    isShown: {
      handler(val) {
        if (val && this.type) {
          setTimeout(() => {
            this.setNotificationConfigByType(this.type);
          }, 100);
        }
      },
    },
    isSidebarOpen: {
      handler() {
        if (this.type === NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR || this.type === NOTIFICATION_POPUP_TYPE.SUPPORT_HINT) {
          setTimeout(() => {
            this.updateSidebarPosition();
          }, 350);
        }
      },
    },
  },
  mounted() {
    window.addEventListener('resize', this.getNewPositionContext);
    if (this.type === NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR || this.type === NOTIFICATION_POPUP_TYPE.SUPPORT_HINT) {
      this.sidebarObserver = new MutationObserver(() => {
        if (this.currentConfig && (this.type === NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR || this.type === NOTIFICATION_POPUP_TYPE.SUPPORT_HINT)) {
          setTimeout(() => {
            this.updateSidebarPosition();
          }, 50);
        }
      });

      const sidebar = document.getElementById('leftSideBar');
      if (sidebar) {
        this.sidebarObserver.observe(sidebar, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.getNewPositionContext);
    if (this.sidebarObserver) {
      this.sidebarObserver.disconnect();
    }
  },
  methods: {
    async setNotificationConfigByType(type) {
      const config = cloneDeep(this.defaultConfigMap[type]);

      if (!config) {
        return;
      }

      const $controlElement = document.getElementById(config.controlNodeId);
      const sidebar = document.getElementById('leftSideBar');

      if (!$controlElement || !sidebar) {
        const retryAttempts = [300, 600, 1000, 1500];
        retryAttempts.forEach((delay, index) => {
          setTimeout(() => {
            const retryElement = document.getElementById(config.controlNodeId);
            const retrySidebar = document.getElementById('leftSideBar');
            if (retryElement && retrySidebar) {
              const sidebarRect = retrySidebar.getBoundingClientRect();
              const isSidebarCollapsed = sidebarRect.width < 100;
              config.isSidebarCollapsed = isSidebarCollapsed;

              if (type === NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR || type === NOTIFICATION_POPUP_TYPE.SUPPORT_HINT) {
                const rect = retryElement.getBoundingClientRect();
                const isSidebarCollapsed = sidebarRect.width < 100;
                config.isSidebarCollapsed = isSidebarCollapsed;
                const leftPos = rect.right + 11;
                config.positionContext = {
                  top: rect.top + rect.height / 2,
                  left: leftPos,
                  width: 0,
                  height: 0,
                  right: leftPos,
                };
              } else {
                config.positionContext = retryElement.getBoundingClientRect();
              }

              this.currentConfig = config;
            }
          }, delay);
        });
        return;
      }

      if (type === NOTIFICATION_POPUP_TYPE.NEWS) {
        config.content = await this.getLastNewsContent();
      }
      const sidebarRect = sidebar.getBoundingClientRect();
      const isSidebarCollapsed = sidebarRect.width < 100;
      config.isSidebarCollapsed = isSidebarCollapsed;

      if (type === NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR || type === NOTIFICATION_POPUP_TYPE.SUPPORT_HINT) {
        const rect = $controlElement.getBoundingClientRect();
        const isSidebarCollapsed = sidebarRect.width < 100;
        config.isSidebarCollapsed = isSidebarCollapsed;

        const leftPos = rect.right + 11;
        config.positionContext = {
          top: rect.top + rect.height / 2,
          left: leftPos,
          width: 0,
          height: 0,
          right: leftPos,
        };
      } else {
        config.positionContext = $controlElement.getBoundingClientRect();
      }

      this.currentConfig = config;
    },
    handleOnCloseNotificationPopup() {
      this.$emit('close');
      this.currentConfig.onCloseClickHandler();
      userExtAnalytics.log('app_notification_popup_closed');
    },
    handleOnClickNotificationPopup({ target }) {
      if (target.closest('.closer-icon')) {
        return;
      }

      this.$emit('onClick', this.currentConfig.attachedToRefName);
    },
    getLastNewsContent() {
      return api.get('/newshistory', {
        params: {
          start: 0,
          count: 1000,
        },
      })
        .then(({ data }) => {
          const { data: newsItems } = data;
          const sortedItems = newsItems.sort((a, b) => b.idTemplate - a.idTemplate);

          if (!sortedItems.length) {
            return '';
          }

          return sortedItems[0].header;
        })
        .catch(console.error);
    },
    toggleControlHighlight(highlightEnabled, event) {
      if (!this.currentConfig) {
        return;
      }

      if (event.target.closest('.closer-icon')) {
        this.$emit('highlight', false);

        return;
      }
      this.$emit('highlight', highlightEnabled);
    },
    getNewPositionContext: throttle(function () {
      if (!this.currentConfig) {
        return;
      }

      const $controlElement = document.getElementById(this.currentConfig.controlNodeId);

      $controlElement && (this.currentConfig.positionContext = $controlElement.getBoundingClientRect());
    }, 50),
    updateSidebarPosition() {
      if (!this.currentConfig || (this.type !== NOTIFICATION_POPUP_TYPE.DEMO_SIDEBAR && this.type !== NOTIFICATION_POPUP_TYPE.SUPPORT_HINT)) {
        return;
      }
      const $controlElement = document.getElementById(this.currentConfig.controlNodeId);
      const sidebar = document.getElementById('leftSideBar');
      if (!$controlElement || !sidebar) return;

      const rect = $controlElement.getBoundingClientRect();
      const sidebarRect = sidebar.getBoundingClientRect();
      const isSidebarCollapsed = sidebarRect.width < 100;

      const leftPos = rect.right + 11;
      const newPositionContext = {
        top: rect.top + rect.height / 2,
        left: leftPos,
        width: 0,
        height: 0,
        right: leftPos,
      };
      this.currentConfig = {
        ...this.currentConfig,
        isSidebarCollapsed,
        positionContext: { ...newPositionContext },
      };
    },
  },
};
</script>

<style lang="less">
.slide-up-enter-active {
  transition: all .6s ease;
}
.slide-up-leave-active {
  transition: all .6s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}
.slide-up-enter, .slide-up-leave-to {
  transform: translateY(20vh);
  opacity: 0;
}

.bounce-side-enter-active {
  animation: bounce-side-in 1s linear both;
}

.bounce-side-leave-active {
  transition: opacity 0.3s ease;
}

.bounce-side-leave-to {
  opacity: 0;
}

@keyframes bounce-side-in {
  0% {
    opacity: 0;
    transform: translateX(90px);
    animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);
  }
  30% {
    opacity: 1;
    transform: translateX(0);
    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
  }
  50% {
    transform: translateX(45px);
    animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);
  }
  70% {
    transform: translateX(0);
    animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);
  }
  85% {
    transform: translateX(15px);
    animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
